import { useRef, useEffect, useCallback } from "react";
import { OneAgruparToolData } from "../types/tools";
import { useApiCache } from "../../../lib/useApiCache";
import { sendToTerminal } from "../../../lib/terminalLogger";

const CACHE_KEY = "tool:agrupar-objetos-sobrepostos";

export interface OneAgruparCacheResult {
	/** Dados da ferramenta carregados */
	toolData: OneAgruparToolData | null;
	/** Indica se está carregando */
	isLoading: boolean;
	/** Mensagem de erro (null se não houver) */
	error: string | null;
	/** Código do erro para tratamento UX */
	errorCode: "network" | "validation" | "auth" | "unknown" | null;
	/** Indica se já carregou com sucesso alguma vez */
	hasLoaded: boolean;
	/** Carrega os dados (com opção de forçar refresh) */
	loadData: (forceRefresh?: boolean) => Promise<OneAgruparToolData | null>;
	/** Limpa o cache e reseta estado */
	clearCache: () => void;
	/** Força refresh dos dados */
	refreshData: () => Promise<void>;
	/** Carrega sob demanda (retorna dados ou null) */
	loadOnDemand: () => Promise<OneAgruparToolData | null>;
	/** Indica se os dados vieram do cache (304 Not Modified) */
	fromCache?: boolean;
	/** ETag atual dos dados */
	etag?: string;
}

/**
 * Hook para gerenciar o cache dos dados da ferramenta OneAgrupar.
 *
 * ✅ Carregamento LAZY (sob demanda) - só carrega quando loadOnDemand() é chamado
 * ✅ Cache inteligente com ETag - só baixa código novo se mudou no servidor
 * ✅ Validação de estrutura dos dados
 * ✅ Retry automático em caso de falha
 * ✅ Tratamento de erros com código para UX
 */
export function useOneAgruparCache(): OneAgruparCacheResult {
	const hasLoadedRef = useRef(false);
	const lastErrorLogRef = useRef<string | null>(null);

	// Usar useApiCache com ETag para cache inteligente
	const {
		data: toolData,
		loading: isLoading,
		error,
		errorCode,
		refresh: refreshData,
		clear,
		reload: loadData,
		hasAttempted,
		fromCache,
		etag,
	} = useApiCache<OneAgruparToolData>(
		CACHE_KEY,
		"/tools/agrupar-objetos-sobrepostos",
		{
			ttlMs: 30 * 60 * 1000, // 30 minutos (alinhado com TTL do backend)
			area: "local", // localStorage para persistir entre sessões
			useETag: true, // ✅ Cache inteligente com ETag - só baixa se mudou
			lazy: true, // ✅ NÃO carrega automaticamente no mount
			maxRetries: 2, // Tentar até 2 vezes em caso de erro
			retryDelayMs: 500, // 500ms entre tentativas
			validate: (data) => {
				// Validar estrutura da resposta
				if (!data || typeof data !== "object") return false;
				if (
					!data.code ||
					typeof data.code !== "string" ||
					data.code.trim().length === 0
				)
					return false;
				if (!data.defaults || typeof data.defaults !== "object") return false;
				if (
					typeof data.defaults.marginTolerancia !== "number" ||
					typeof data.defaults.prefixoNomeGrupo !== "string" ||
					typeof data.defaults.usarNomenclaturaNumerica !== "boolean"
				) {
					return false;
				}
				return true;
			},
		},
	);

	// Marcar como carregado quando dados estiverem disponíveis
	useEffect(() => {
		if (toolData && !hasLoadedRef.current) {
			hasLoadedRef.current = true;
		}
	}, [toolData]);

	// Log de erro com debounce (não loga o mesmo erro repetidamente)
	useEffect(() => {
		if (error && error !== lastErrorLogRef.current) {
			lastErrorLogRef.current = error;
			sendToTerminal("❌ [ONEAGRUPAR] Erro ao carregar dados", {
				error,
				errorCode,
				hasAttempted,
			});
		} else if (!error) {
			lastErrorLogRef.current = null;
		}
	}, [error, errorCode, hasAttempted]);

	// Log de cache hit para debug
	useEffect(() => {
		if (fromCache !== undefined && toolData) {
			sendToTerminal(
				fromCache
					? "🟢 [ONEAGRUPAR] Cache HIT (304)"
					: "🔵 [ONEAGRUPAR] Cache MISS",
				{ etag, hasData: !!toolData },
			);
		}
	}, [fromCache, etag, toolData]);

	const clearCache = useCallback(() => {
		clear();
		hasLoadedRef.current = false;
		lastErrorLogRef.current = null;
	}, [clear]);

	const loadOnDemand =
		useCallback(async (): Promise<OneAgruparToolData | null> => {
			// Se já tem dados carregados, retornar imediatamente
			if (hasLoadedRef.current && toolData) {
				return toolData;
			}

			// Carregar dados
			await loadData(false);

			// Aguardar um ciclo para o estado React atualizar
			await new Promise((resolve) => setTimeout(resolve, 100));

			// Marcar como carregado se tiver dados
			if (toolData) {
				hasLoadedRef.current = true;
			}

			return toolData ?? null;
		}, [toolData, loadData]);

	const loadDataWithReturn = useCallback(
		async (forceRefresh = false): Promise<OneAgruparToolData | null> => {
			if (forceRefresh) {
				await refreshData();
			} else {
				await loadData(false);
			}

			// Aguardar atualização do estado
			await new Promise((resolve) => setTimeout(resolve, 50));

			return toolData ?? null;
		},
		[refreshData, loadData, toolData],
	);

	return {
		toolData,
		isLoading,
		error,
		errorCode,
		hasLoaded: hasLoadedRef.current,
		loadData: loadDataWithReturn,
		clearCache,
		refreshData,
		loadOnDemand,
		fromCache,
		etag,
	};
}
